import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import comptabiliteService, { Rapprochement, RapprochementDetail } from '../../api/comptabilite';
import { formatTND } from '../../utils/formatters';

const today = () => new Date().toISOString().slice(0, 10);

const RapprochementBancaire: React.FC = () => {
  const [items, setItems] = useState<Rapprochement[]>([]);
  const [detail, setDetail] = useState<RapprochementDetail | null>(null);
  const [form, setForm] = useState({ compteNumero: '532', libelle: '', dateRapprochement: today(), soldeReleve: '' });

  const refresh = async () => {
    try {
      setItems(await comptabiliteService.listRapprochements());
    } catch (e: any) {
      if (e?.response?.status !== 403) toast.error('Chargement impossible.');
    }
  };
  useEffect(() => { refresh(); }, []);

  const creer = async () => {
    try {
      const r = await comptabiliteService.createRapprochement({
        compteNumero: form.compteNumero,
        libelle: form.libelle || undefined,
        dateRapprochement: form.dateRapprochement,
        soldeReleve: Number(form.soldeReleve) || 0,
      });
      toast.success('Rapprochement créé.');
      setForm({ ...form, libelle: '', soldeReleve: '' });
      refresh();
      ouvrir(r.id);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Création impossible.');
    }
  };

  const ouvrir = async (id: number) => {
    try {
      setDetail(await comptabiliteService.rapprochementDetail(id));
    } catch (e: any) {
      toast.error('Détail indisponible.');
    }
  };

  const pointer = async (ligneId: number, pointe: boolean) => {
    if (!detail) return;
    try {
      setDetail(await comptabiliteService.pointer(detail.rapprochement.id, ligneId, pointe));
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Pointage impossible.');
    }
  };

  const cloturer = async () => {
    if (!detail) return;
    try {
      await comptabiliteService.cloturerRapprochement(detail.rapprochement.id);
      toast.success('Rapprochement clôturé.');
      ouvrir(detail.rapprochement.id);
      refresh();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Clôture impossible.');
    }
  };

  const supprimer = async (r: Rapprochement) => {
    if (!window.confirm('Supprimer ce rapprochement ?')) return;
    try {
      await comptabiliteService.deleteRapprochement(r.id);
      if (detail?.rapprochement.id === r.id) setDetail(null);
      refresh();
    } catch (e: any) {
      toast.error('Suppression impossible.');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <Link to="/comptabilite" className="text-sm text-blue-600 hover:underline">← Comptabilité</Link>
        <h1 className="text-2xl font-bold text-gray-900 mt-1">Rapprochement bancaire</h1>
        <p className="text-sm text-gray-500">Pointez les mouvements du compte de banque contre le solde du relevé.</p>
      </div>

      <div className="bg-white p-6 rounded-xl border border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Nouveau rapprochement</h2>
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Compte</label>
            <input value={form.compteNumero} onChange={(e) => setForm({ ...form, compteNumero: e.target.value })}
                   className="rounded-lg border border-gray-200 px-3 py-2 w-24" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Libellé</label>
            <input value={form.libelle} onChange={(e) => setForm({ ...form, libelle: e.target.value })}
                   className="rounded-lg border border-gray-200 px-3 py-2" placeholder="Relevé janvier" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Date</label>
            <input type="date" value={form.dateRapprochement} onChange={(e) => setForm({ ...form, dateRapprochement: e.target.value })}
                   className="rounded-lg border border-gray-200 px-3 py-2" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Solde du relevé</label>
            <input type="number" value={form.soldeReleve} onChange={(e) => setForm({ ...form, soldeReleve: e.target.value })}
                   className="rounded-lg border border-gray-200 px-3 py-2 w-36" />
          </div>
          <button onClick={creer}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700">Créer</button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500">
            <tr>
              <th className="text-left px-4 py-3">Date</th>
              <th className="text-left px-4 py-3">Compte</th>
              <th className="text-left px-4 py-3">Libellé</th>
              <th className="text-right px-4 py-3">Solde relevé</th>
              <th className="text-left px-4 py-3">Statut</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.id} className="border-t border-gray-100">
                <td className="px-4 py-3 text-gray-600">{r.dateRapprochement}</td>
                <td className="px-4 py-3 text-gray-600">{r.compteNumero}</td>
                <td className="px-4 py-3 text-gray-900">{r.libelle || '—'}</td>
                <td className="px-4 py-3 text-right">{formatTND(r.soldeReleve)}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                    r.statut === 'CLOTURE' ? 'bg-gray-200 text-gray-700' : 'bg-amber-100 text-amber-700'}`}>
                    {r.statut === 'CLOTURE' ? 'Clôturé' : 'En cours'}
                  </span>
                </td>
                <td className="px-4 py-3 text-right space-x-2 whitespace-nowrap">
                  <button onClick={() => ouvrir(r.id)} className="text-blue-600 hover:underline">Ouvrir</button>
                  <button onClick={() => supprimer(r)} className="text-red-600 hover:underline">Suppr.</button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">Aucun rapprochement.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {detail && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 flex flex-wrap justify-between items-center gap-2">
            <h3 className="font-semibold text-gray-900">
              Pointage — compte {detail.rapprochement.compteNumero} au {detail.rapprochement.dateRapprochement}
            </h3>
            <button onClick={() => setDetail(null)} className="text-sm text-gray-400 hover:text-gray-600">Fermer</button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-4 text-sm">
            <div><div className="text-gray-500">Solde comptable</div><div className="font-semibold">{formatTND(detail.soldeComptable)}</div></div>
            <div><div className="text-gray-500">Solde pointé</div><div className="font-semibold">{formatTND(detail.soldePointe)}</div></div>
            <div><div className="text-gray-500">Solde relevé</div><div className="font-semibold">{formatTND(detail.rapprochement.soldeReleve)}</div></div>
            <div>
              <div className="text-gray-500">Écart relevé / pointé</div>
              <div className={`font-semibold ${detail.rapproche ? 'text-green-600' : 'text-red-600'}`}>{formatTND(detail.ecart)}</div>
            </div>
          </div>

          {detail.rapprochement.statut === 'EN_COURS' && (
            <div className="px-4 pb-2">
              <button onClick={cloturer} disabled={!detail.rapproche}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 disabled:opacity-50">
                Clôturer le rapprochement
              </button>
              {!detail.rapproche && <span className="ml-3 text-sm text-gray-500">Pointez les lignes jusqu'à un écart nul.</span>}
            </div>
          )}

          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500">
              <tr>
                <th className="px-3 py-3 w-10"></th>
                <th className="text-left px-3 py-3">Date</th>
                <th className="text-left px-3 py-3">Écriture</th>
                <th className="text-left px-3 py-3">Libellé</th>
                <th className="text-right px-3 py-3">Débit</th>
                <th className="text-right px-3 py-3">Crédit</th>
              </tr>
            </thead>
            <tbody>
              {detail.lignes.map((l) => (
                <tr key={l.ligneId} className={`border-t border-gray-100 ${l.pointe ? 'bg-green-50' : ''}`}>
                  <td className="px-3 py-2 text-center">
                    <input type="checkbox" checked={l.pointe}
                           disabled={detail.rapprochement.statut === 'CLOTURE'}
                           onChange={(e) => pointer(l.ligneId, e.target.checked)} />
                  </td>
                  <td className="px-3 py-2 text-gray-600">{l.date}</td>
                  <td className="px-3 py-2 text-gray-600">{l.ecritureNumero}</td>
                  <td className="px-3 py-2 text-gray-900">{l.libelle}</td>
                  <td className="px-3 py-2 text-right">{Number(l.debit) ? formatTND(l.debit) : ''}</td>
                  <td className="px-3 py-2 text-right">{Number(l.credit) ? formatTND(l.credit) : ''}</td>
                </tr>
              ))}
              {detail.lignes.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">Aucun mouvement sur ce compte.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default RapprochementBancaire;
